import { GemIcon, ShoppingCart } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { SecondaryButtonMedium } from '@/components/base/Buttons';
import {
  TypographySectionLabel,
  TypographySmall,
  TypographyText,
  TypographyTitle,
} from '@/components/base/Typography';
import { DiamondIcon } from '@/components/diamonds/DiamondIcon';
import { Button } from '@/components/ui/button';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { getCartCount } from '@/utils/cart';

// Map shape slug → DiamondIcon frame key
const SHAPE_ICON_MAP = {
  round: 'frame-1',
  oval: 'frame-5',
  cushion: 'frame-26',
  emerald: 'frame-19',
  princess: 'frame-11',
  radiant: 'frame-15',
  pear: 'frame-3',
  marquise: 'frame-14',
  asscher: 'frame-8',
  heart: 'frame-25',
};

// Reusable list for shape categories
const ShapeCategoryList = ({ basePath }: { basePath: string }) => (
  <ul className="mt-3 grid list-none grid-cols-2 gap-x-1 gap-y-2 p-0">
    {Object.entries(SHAPE_ICON_MAP).map(([shape, icon]) => (
      <li key={shape} className="flex items-center gap-2">
        <DiamondIcon name={icon} size={20} />
        <Link href={`/${basePath}/${shape}`} legacyBehavior passHref>
          <NavigationMenuLink className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block rounded-md p-2">
            <TypographyText className="leading-none capitalize">{shape}</TypographyText>
          </NavigationMenuLink>
        </Link>
      </li>
    ))}
  </ul>
);

// Reusable list for diamond type categories
const DiamondTypeList = ({ items }) => (
  <ul className="mt-2 space-y-1">
    {items.map(({ icon, label, href }) => (
      <li key={href}>
        <Link href={href} legacyBehavior passHref>
          <NavigationMenuLink className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block rounded-md p-2">
            <div className="flex items-center gap-2">
              {icon}
              <TypographyText>{label}</TypographyText>
            </div>
          </NavigationMenuLink>
        </Link>
      </li>
    ))}
  </ul>
);

// Reusable list for carat categories
const CARAT_VALUES = [0.5, 1, 1.5, 2, 3];
const CaratCategoryList = ({ isLab, onCaratClick }) => (
  <ul className="mt-2 space-y-1 text-left">
    {CARAT_VALUES.map((carat) => (
      <li key={carat}>
        <button
          onClick={() => onCaratClick(carat, isLab)}
          className="hover:bg-accent hover:text-accent-foreground block w-full rounded-md p-2 text-left focus:outline-none"
        >
          <TypographyText>
            {isLab
              ? `${carat === 3 ? '3+' : carat} Carat Lab Diamonds`
              : `${carat === 3 ? '3+' : carat} Carat Natural Diamonds`}
          </TypographyText>
        </button>
      </li>
    ))}
  </ul>
);

// Define ring styles (with images) for navigation – sourced from settings.tsx
const RING_STYLE_CATEGORIES = [
  { id: 'solitaire', name: 'Solitaire', image: '/images/rings/white-gold.png' },
  { id: 'pave', name: 'Pavé', image: '/images/rings/side-stone.png' },
  { id: 'three-stone', name: 'Three-Stone', image: '/images/rings/three-stone.png' },
  { id: 'accent', name: 'Accent', image: '/images/rings/accent.png' },
  { id: 'cluster', name: 'Cluster', image: '/images/rings/cluster.png' },
  { id: 'wave', name: 'Wave', image: '/images/rings/wave.png' },
  { id: 'halo', name: 'Halo', image: '/images/rings/halo.png' },
  { id: 'bezel', name: 'Bezel', image: '/images/rings/bezel.png' },
  { id: 'cascade', name: 'Cascade', image: '/images/rings/cascade.png' },

  { id: 'embrace', name: 'Embrace', image: '/images/rings/embrace.png' },
];

// Reusable list for ring style categories (with images)
const RingStyleList = () => (
  <ul className="mt-2 grid list-none grid-cols-2 gap-1 p-0">
    {RING_STYLE_CATEGORIES.map(({ id, name, image }) => (
      <li key={id} className="flex items-center gap-2">
        {/* Style thumbnail */}
        <img src={image} alt={name} className="h-6 w-6 rounded-full object-cover" />
        <Link href={`/rings/${id}`} legacyBehavior passHref>
          <NavigationMenuLink className="hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground block rounded-md p-2">
            <TypographyText className="capitalize">{name}</TypographyText>
          </NavigationMenuLink>
        </Link>
      </li>
    ))}
  </ul>
);

// Add an inline component that renders the quiz link centred on a full row
const QuizTriggerRow = () => (
  <div className="-mt-4 flex w-full flex-col items-center space-y-1">
    <div className="flex w-full items-center">
      <div className="flex-grow border-t" />
      <TypographyText className="text-muted-foreground mx-2">Explore</TypographyText>
      <div className="flex-grow border-t" />
    </div>
    <Link href="/quiz" legacyBehavior passHref>
      <NavigationMenuLink className="flex flex-row items-center gap-2 p-2">
        <GemIcon className="h-4 w-4" />
        <TypographyText>Start with a diamond quiz</TypographyText>
      </NavigationMenuLink>
    </Link>
  </div>
);

export function Header() {
  const router = useRouter();
  const cartCount = typeof window !== 'undefined' ? getCartCount() : 0;

  const handleCaratClick = (carat: number, isLab: boolean) => {
    if (typeof window !== 'undefined') {
      try {
        // Store only the lower-bound value; the upper bound will be filled in by useDiamondsWithShape
        sessionStorage.setItem('diamond_carat_filter', JSON.stringify(carat));
        // console.log('Header: set diamond_carat_filter to', carat);

        // Notify any listening components (e.g. diamond catalogue) so they can
        // immediately react even if the current route doesn't change.
        window.dispatchEvent(new CustomEvent('diamond_carat_filter_set'));
      } catch {}
    }
    // Navigate to the relevant catalogue page (no query params required)
    router.push(isLab ? '/lab-diamonds' : '/diamonds');
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white">
      <div className="container mx-auto flex h-20 items-center justify-between px-4">
        {/* Logo */}
        <Link href="/" className="flex items-center space-x-2">
          <TypographyTitle className="text-primary mt-6 leading-none">
            Dream Diamonds
          </TypographyTitle>
        </Link>

        {/* Main navigation */}
        <NavigationMenu className="ml-4 hidden justify-center lg:flex">
          <NavigationMenuList className="gap-1">
            {/* Diamonds */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <TypographyText>Diamonds</TypographyText>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="grid w-[800px] gap-6 p-6 md:grid-cols-3">
                  <div>
                    <TypographySectionLabel>Shop by Shape</TypographySectionLabel>
                    <ShapeCategoryList basePath="diamonds" />
                  </div>
                  <div>
                    <TypographySectionLabel>Shop by Diamond</TypographySectionLabel>
                    <DiamondTypeList
                      items={[
                        {
                          icon: <GemIcon className="h-4 w-4" />,
                          label: 'Natural Diamonds',
                          href: '/diamonds',
                        },
                        {
                          icon: <GemIcon className="h-4 w-4" />,
                          label: 'Lab Diamonds',
                          href: '/lab-diamonds',
                        },
                      ]}
                    />
                  </div>
                  <div>
                    <TypographySectionLabel>Shop by Carat</TypographySectionLabel>
                    <CaratCategoryList isLab={false} onCaratClick={handleCaratClick} />
                  </div>
                </div>
                <QuizTriggerRow />
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Lab Diamonds */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <TypographyText>Lab Diamonds</TypographyText>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="grid w-[800px] gap-6 p-6 md:grid-cols-3">
                  <div>
                    <TypographySectionLabel>Shop by Shape</TypographySectionLabel>
                    <ShapeCategoryList basePath="lab-diamonds" />
                  </div>
                  <div>
                    <TypographySectionLabel>Shop by Diamond</TypographySectionLabel>
                    <DiamondTypeList
                      items={[
                        {
                          icon: <GemIcon className="h-4 w-4" />,
                          label: 'Lab Diamonds',
                          href: '/lab-diamonds',
                        },
                        {
                          icon: <GemIcon className="h-4 w-4" />,
                          label: 'Natural Diamonds',
                          href: '/diamonds',
                        },
                      ]}
                    />
                  </div>
                  <div>
                    <TypographySectionLabel>Shop by Carat</TypographySectionLabel>
                    <CaratCategoryList isLab={true} onCaratClick={handleCaratClick} />
                  </div>
                </div>
                <QuizTriggerRow />
              </NavigationMenuContent>
            </NavigationMenuItem>

            {/* Rings */}
            <NavigationMenuItem>
              <NavigationMenuTrigger>
                <TypographyText>Rings</TypographyText>
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <div className="grid w-[700px] gap-6 p-6 md:grid-cols-2">
                  <div>
                    <TypographySectionLabel>Shop by Shape</TypographySectionLabel>
                    <ShapeCategoryList basePath="rings" />
                  </div>
                  <div>
                    <TypographySectionLabel>Shop by Style</TypographySectionLabel>
                    <RingStyleList />
                  </div>
                </div>
                <QuizTriggerRow />
              </NavigationMenuContent>
            </NavigationMenuItem>

            <NavigationMenuItem>
              <Link href="/education" legacyBehavior passHref>
                <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                  <TypographyText>Education</TypographyText>
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
            <NavigationMenuItem>
              <Link href="/about" legacyBehavior passHref>
                <NavigationMenuLink className={navigationMenuTriggerStyle()}>
                  <TypographyText>About Us</TypographyText>
                </NavigationMenuLink>
              </Link>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>

        {/* Right side actions */}
        <div className="mr-0 flex items-center gap-4 pr-0 lg:pr-1">
          <Button variant="ghost" size="icon" asChild className="relative">
            <Link href="/cart">
              <ShoppingCart className="h-5 w-5" />
              {cartCount > 0 && (
                <div className="bg-primary absolute -top-1 flex h-5 w-5 items-center justify-center rounded-full">
                  <TypographySmall className="text-xs text-white">{cartCount}</TypographySmall>
                </div>
              )}
              <TypographySmall className="sr-only">Cart</TypographySmall>
            </Link>
          </Button>

          <Link href="/diamonds/round" passHref>
            <SecondaryButtonMedium className="hidden sm:inline-flex">
              Shop Now
            </SecondaryButtonMedium>
          </Link>
        </div>
      </div>
    </header>
  );
}
